<script setup>
/* global axios */
// Reações de mensagem estilo WhatsApp (dentro do Chatwoot).
// - Mostra os emojis reagidos (badge no cantinho do balão), do cliente e do agente.
// - Ao passar o mouse, aparece um botão de reação → barra rápida (👍❤️😂😮😢🙏) + "+".
// - "+" abre o picker completo. Selecionar → manda a reação pro WhatsApp (AstraCall).
// Envolve o balão via slot. Dados vêm de content_attributes.reactions.
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { OnClickOutside } from '@vueuse/components';
import { useMapGetter } from 'dashboard/composables/store';
import { useMessageContext } from './provider.js';
import { ORIENTATION } from './constants';
import { emojiSrc } from 'shared/helpers/emojitwo';
import EmojiPicker from 'shared/components/emoji/EmojiPicker.vue';

const { id, contentAttributes, orientation, isPrivate, inboxId, messageType } =
  useMessageContext();
const route = useRoute();
const getInbox = useMapGetter('inboxes/getInbox');

const QUICK = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const showBar = ref(false);
const showPicker = ref(false);
const sending = ref(false);

// reação (round-trip WhatsApp) — habilitada nos provedores que têm endpoint:
// AstraCall, Evolution e WAHA.
const REACTION_PROVIDERS = ['astracall', 'evolution', 'evolution-go', 'waha'];
const reactable = computed(() => {
  if (isPrivate?.value) return false;
  if (messageType?.value === 2) return false; // activity
  const inbox = getInbox.value(inboxId?.value);
  return REACTION_PROVIDERS.includes(inbox?.kanban_provider);
});

const isRight = computed(() => orientation?.value === ORIENTATION.RIGHT);

const reactions = computed(() => contentAttributes?.value?.reactions || []);

// agrupa por emoji (emoji → { count, mine })
const grouped = computed(() => {
  const map = new Map();
  reactions.value.forEach(r => {
    if (!r?.emoji) return;
    const cur = map.get(r.emoji) || { emoji: r.emoji, count: 0, mine: false };
    cur.count += 1;
    if (r.fromMe) cur.mine = true;
    map.set(r.emoji, cur);
  });
  return [...map.values()];
});

const myEmoji = computed(() => reactions.value.find(r => r.fromMe)?.emoji || '');

const react = async emoji => {
  if (sending.value) return;
  showBar.value = false;
  showPicker.value = false;
  // toggle: clicar no mesmo emoji que já reagi → remove
  const value = myEmoji.value === emoji ? '' : emoji;
  sending.value = true;
  try {
    await axios.post(
      `/api/v1/accounts/${route.params.accountId}/kanban/connections/react`,
      { message_id: id?.value, emoji: value }
    );
  } catch (e) {
    // silencioso — o estado real volta pelo update em tempo real
  } finally {
    sending.value = false;
  }
};

const onPick = payload => react(payload.emoji || payload.value);
</script>

<template>
  <div class="flex flex-col group/react" :class="isRight ? 'items-end' : 'items-start'">
    <div
      class="flex items-center gap-1"
      :class="isRight ? 'flex-row-reverse' : 'flex-row'"
    >
      <slot />

      <!-- gatilho de reação (aparece no hover) -->
      <div v-if="reactable" class="relative shrink-0">
        <button
          type="button"
          class="flex items-center justify-center transition-opacity rounded-full opacity-0 size-8 text-n-slate-11 bg-n-solid-2 shadow-sm outline outline-1 outline-n-weak hover:bg-n-alpha-2 group-hover/react:opacity-100"
          :class="{ 'opacity-100': showBar || showPicker }"
          @click="showBar = !showBar"
        >
          <span class="i-ph-smiley size-5" />
        </button>

        <!-- barra rápida (estilo WhatsApp: pill branca, pop-in, emojis crescem no hover) -->
        <Transition name="react-pop">
          <OnClickOutside
            v-if="showBar"
            class="absolute z-20 -translate-y-2 bottom-full react-bar-origin"
            :class="isRight ? 'right-0' : 'left-0'"
            @trigger="showBar = false"
          >
            <div
              class="flex items-center gap-0.5 px-1.5 py-1 rounded-full shadow-xl bg-n-solid-1 outline outline-1 outline-n-weak backdrop-blur"
            >
              <button
                v-for="(e, i) in QUICK"
                :key="e"
                type="button"
                class="flex items-center justify-center leading-none transition-transform duration-150 rounded-full size-9 origin-bottom react-emoji hover:scale-[1.35] active:scale-95"
                :class="myEmoji === e ? 'bg-n-brand/10 ring-2 ring-n-brand scale-110' : 'hover:bg-n-alpha-1'"
                :style="{ animationDelay: `${i * 25}ms` }"
                @click="react(e)"
              >
                <img :src="emojiSrc(e)" alt="" class="pointer-events-none size-7" />
              </button>
              <button
                type="button"
                class="flex items-center justify-center transition-colors rounded-full size-9 shrink-0 text-n-slate-11 bg-n-alpha-2 hover:bg-n-alpha-3 hover:text-n-slate-12"
                @click="showPicker = true; showBar = false"
              >
                <span class="i-ph-plus-bold size-4" />
              </button>
            </div>
          </OnClickOutside>
        </Transition>

        <!-- picker completo -->
        <OnClickOutside v-if="showPicker" @trigger="showPicker = false">
          <div
            class="absolute z-30 -translate-y-1 bottom-full"
            :class="isRight ? 'right-0' : 'left-0'"
          >
            <EmojiPicker @select="onPick" />
          </div>
        </OnClickOutside>
      </div>
    </div>

    <!-- badges das reações -->
    <TransitionGroup
      v-if="grouped.length"
      tag="div"
      name="react-badge"
      class="flex flex-wrap gap-1 -mt-2 z-10"
      :class="isRight ? 'justify-end mr-1' : 'ml-1'"
    >
      <button
        v-for="g in grouped"
        :key="g.emoji"
        type="button"
        class="flex items-center gap-0.5 pl-1 pr-1.5 h-[22px] text-xs rounded-full shadow-sm bg-n-solid-1 outline outline-1 transition-transform hover:scale-105 active:scale-95"
        :class="g.mine ? 'outline-n-brand bg-n-brand/5' : 'outline-n-weak'"
        @click="reactable && react(g.emoji)"
      >
        <img :src="emojiSrc(g.emoji)" alt="" class="react-badge-emoji size-[16px] object-contain" />
        <span v-if="g.count > 1" class="font-medium text-n-slate-11 tabular-nums">{{ g.count }}</span>
      </button>
    </TransitionGroup>
  </div>
</template>

<style scoped>
/* barra rápida: pop-in com leve subida (estilo WhatsApp) */
.react-pop-enter-active {
  transition: transform 0.18s cubic-bezier(0.2, 1.5, 0.4, 1), opacity 0.14s ease;
}
.react-pop-leave-active {
  transition: transform 0.12s ease, opacity 0.12s ease;
}
.react-pop-enter-from,
.react-pop-leave-to {
  opacity: 0;
  transform: translateY(0.25rem) scale(0.7);
}
.react-bar-origin {
  transform-origin: bottom center;
}

/* cada emoji sobe escalonado quando a barra aparece */
.react-emoji {
  animation: react-rise 0.28s cubic-bezier(0.2, 1.4, 0.4, 1) both;
}
@keyframes react-rise {
  from {
    opacity: 0;
    transform: translateY(0.5rem) scale(0.5);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* badge entra com um pop */
.react-badge-enter-active {
  transition: transform 0.22s cubic-bezier(0.2, 1.5, 0.4, 1), opacity 0.18s ease;
}
.react-badge-leave-active {
  transition: transform 0.15s ease, opacity 0.15s ease;
  position: absolute;
}
.react-badge-enter-from,
.react-badge-leave-to {
  opacity: 0;
  transform: scale(0.4);
}

@media (prefers-reduced-motion: reduce) {
  .react-emoji,
  .react-pop-enter-active,
  .react-pop-leave-active,
  .react-badge-enter-active,
  .react-badge-leave-active {
    animation: none;
    transition: opacity 0.1s ease;
  }
}
</style>
